import { useParams, Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import FloatingLanguageSwitcher from '../components/FloatingLanguageSwitcher';
import { Card } from '../components/ui';

interface VerifyResult {
  valid: boolean;
  score_percent?: number;
  exam_date?: string;
}

async function verifyCertificate(hash: string): Promise<VerifyResult> {
  const { data } = await apiClient.get<VerifyResult>(`/api/verify/${hash}`);
  return data;
}

export default function VerifyCertificatePage() {
  const { t, i18n } = useTranslation();
  const { hash } = useParams<{ hash: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['verify-certificate', hash],
    queryFn: () => verifyCertificate(hash!),
    enabled: !!hash,
    retry: false,
  });

  const dateLocale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
      <FloatingLanguageSwitcher />
      <main id="main-content" tabIndex={-1} className="flex flex-1 items-center justify-center px-4 outline-none">
        <div className="w-full max-w-md text-center">
          <Card className="space-y-6 p-8">
            <span className="text-5xl" aria-hidden="true">&#128220;</span>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {t('legal.verifyCert.title')}
            </h1>

            {isLoading && (
              <div className="flex items-center justify-center py-6" role="status">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent" />
                <span className="sr-only">{t('legal.verifyCert.loadingSr')}</span>
              </div>
            )}

            {error && (
              <div className="space-y-3">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/15">
                  <svg
                    className="h-8 w-8 text-red-600 dark:text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <p className="text-lg font-semibold text-red-600 dark:text-red-400">{t('legal.verifyCert.invalidTitle')}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('legal.verifyCert.invalidLong')}
                </p>
              </div>
            )}

            {data?.valid && (
              <div className="space-y-3">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-500/15">
                  <svg
                    className="h-8 w-8 text-green-600 dark:text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
                <p className="text-lg font-semibold text-green-600 dark:text-green-400">{t('legal.verifyCert.validTitle')}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('legal.verifyCert.validBody')}
                </p>
                {data.score_percent != null && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    <Trans
                      i18nKey="legal.verifyCert.score"
                      values={{ score: data.score_percent }}
                      components={{ strong: <strong /> }}
                    />
                  </p>
                )}
                {data.exam_date && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('legal.verifyCert.issued', {
                      date: new Date(data.exam_date).toLocaleDateString(dateLocale),
                    })}
                  </p>
                )}
              </div>
            )}

            {data && !data.valid && (
              <div className="space-y-3">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/15">
                  <svg
                    className="h-8 w-8 text-red-600 dark:text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <p className="text-lg font-semibold text-red-600 dark:text-red-400">{t('legal.verifyCert.invalidTitle')}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('legal.verifyCert.invalidShort')}
                </p>
              </div>
            )}

            <Link to="/" className="inline-block text-sm text-blue-600 hover:underline dark:text-blue-400">
              {t('legal.verifyCert.goHome')}
            </Link>
          </Card>
        </div>
      </main>
    </div>
  );
}
